import React, { useState, useEffect, useMemo } from "react";
import { Autocomplete, TextField, CircularProgress, Checkbox, Typography, Stack, InputLabel } from "@mui/material";
import { debounce } from "lodash";
import { GlobalState } from "context/GlobalContext";
import { constraintsOptions } from "components/PimDashboard/data";
import { inputTypes } from "data/globalData";
import { grey } from "@mui/material/colors";
import { parseErrorMessage } from "utils/ErrorFunctions/parseErrorMessage";
import { LightTooltip } from "../CustomToolTip/CustomToolTip";

const SELECT_ALL = { key: "__select_all__", name: "Select All" };

/**
 * storeValueAs: "key" | "object"
 * - "key": single -> string, multiple -> string[]
 * - "object": single -> object, multiple -> object[]
 */
const SearchInputFieldWithDebounce = ({
	column,
	data,
	setData,
	fetchData,
	selectOptKeyName,
	disableLabel = false,
	width = "100%",
	storeValueAs = "key", // "key" | "object"
	disabled = false,
	sx = {},
	error = false,
	labelSx = {},
	isHorizontalLayout = false,
	onChange,
}) => {
	const { setSnackBar, setSnackBarOpen, snackBar, setDropdownDataCache, getDropdownCache } = GlobalState();

	const required = !!column?.constraints?.[constraintsOptions.NOTNULL.key];

	const [query, setQuery] = useState("");
	const [loading, setLoading] = useState(false);
	const [optionsList, setOptionsList] = useState([]);

	const multiple = column.input_type === inputTypes.MULTIPLE_SELECT.key;

	// raw value from parent data (may be key or object depending on previous saves)
	const rawValue = data?.[column.key];

	// Normalize option items (merge any label info from column[selectOptKeyName].getOptionLabel)
	const normalize = (list = []) => {
		return list.map((opt) => {
			if (typeof column?.[selectOptKeyName]?.getOptionLabel === "function") {
				// If user returns extra label props from their helper, merge them
				return { ...opt, ...column[selectOptKeyName]?.getOptionLabel(opt) };
			} else {
				return opt;
			}
		});
	};

	const removeDuplicates = (list = []) =>
		Object.values(
			list.reduce((acc, row) => {
				if (row.key !== SELECT_ALL.key) acc[row.key] = row;
				return acc;
			}, {}),
		);

	const fetchDefaultData = async () => {
		const cacheKey = column.key;
		const cached = getDropdownCache(cacheKey);
		if (cached) {
			setOptionsList(multiple ? [SELECT_ALL, ...cached] : cached);
			return;
		}

		setLoading(true);
		try {
			const res = await fetchData();
			const result = res?.data?.result ?? [];
			const normalized = normalize(result);
			setOptionsList(multiple ? [SELECT_ALL, ...normalized] : normalized);
			setDropdownDataCache(cacheKey, normalized);
		} catch (err) {
			setSnackBar({
				...snackBar,
				message: parseErrorMessage(err),
				severity: "error",
			});
			setSnackBarOpen(true);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		fetchDefaultData();
	}, [column.key]);

	const fetchSuggestions = async (searchTerm) => {
		if (!searchTerm.trim()) return;
		setLoading(true);
		try {
			const res = await fetchData(0, 20, searchTerm);
			const result = res?.data?.result ?? [];
			const normalized = normalize(result);
			setOptionsList((prev) =>
				multiple
					? [SELECT_ALL, ...removeDuplicates([...normalized, ...prev.filter((row) => row.key !== SELECT_ALL.key)])]
					: removeDuplicates([...normalized, ...prev.filter((row) => row.key !== SELECT_ALL.key)]),
			);
		} catch (error) {
			console.error("Error fetching suggestions:", error);
		} finally {
			setLoading(false);
		}
	};

	const debouncedFetch = useMemo(
		() => debounce(fetchSuggestions, 800),
		[
			/* intentionally stable */
		],
	);

	useEffect(() => {
		if (query) debouncedFetch(query);
	}, [query, debouncedFetch]);

	useEffect(() => {
		return () => debouncedFetch.cancel();
	}, [debouncedFetch]);

	// helpers
	const effectiveOptions = optionsList.filter((o) => o.key !== SELECT_ALL.key);
	const optionFromKey = (k) => optionsList.find((o) => o.key === k);

	// compute selected keys array (for select-all logic)
	const selectedKeysArray = useMemo(() => {
		if (!multiple) return [];
		if (!rawValue) return [];
		if (storeValueAs === "object") {
			if (!Array.isArray(rawValue)) return [];
			return rawValue.map((v) => (typeof v === "object" ? v.key : v));
		} else {
			// key mode
			return Array.isArray(rawValue) ? rawValue : [];
		}
	}, [rawValue, multiple, storeValueAs]);

	const isAllSelected =
		multiple &&
		effectiveOptions.length > 0 &&
		selectedKeysArray.length > 0 &&
		selectedKeysArray.length === effectiveOptions.length;

	// Map parent's rawValue to Autocomplete's value prop
	const value = useMemo(() => {
		if (!multiple) {
			// single select
			if (rawValue == null || rawValue === "") return null;

			if (storeValueAs === "object") {
				// rawValue might be an object or a key (if previous state was different)
				if (typeof rawValue === "object" && rawValue.key !== undefined) {
					// prefer the object from optionsList (so references match MUI), fall back to rawValue
					const found = optionsList.find((o) => o.key === rawValue.key);
					return found ?? rawValue;
				} else {
					// raw value is a primitive key
					return optionFromKey(rawValue) ?? { key: rawValue, name: String(rawValue) };
				}
			} else {
				// storeValueAs === "key"
				return optionFromKey(rawValue) ?? null;
			}
		} else {
			// multiple
			if (!Array.isArray(rawValue)) return [];

			if (storeValueAs === "object") {
				// rawValue may contain objects and/or keys; map to option objects where possible
				return rawValue.map((v) => {
					if (typeof v === "object" && v.key !== undefined) {
						return optionsList.find((o) => o.key === v.key) ?? v;
					} else {
						return optionFromKey(v) ?? { key: v, name: String(v) };
					}
				});
			} else {
				// key mode -> return filtered option objects matching stored keys
				return optionsList.filter((opt) => rawValue.includes(opt.key));
			}
		}
	}, [rawValue, optionsList, multiple, storeValueAs, optionFromKey]);

	// improved equality checker (handles string keys, objects, arrays)
	const isOptionEqualToValue = (opt, val) => {
		if (!opt) return false;
		if (val == null) return false;

		// primitive (string/number) value
		if (typeof val === "string" || typeof val === "number") return opt.key === val;

		// array (multiple's internal value sometimes)
		if (Array.isArray(val)) {
			return val.some((v) => {
				if (!v) return false;
				if (typeof v === "string" || typeof v === "number") return opt.key === v;
				return opt.key === (v.key ?? v);
			});
		}

		// object
		if (typeof val === "object") return opt.key === (val.key ?? val);

		return false;
	};

	// display label getter
	const getLabel = (option) => {
		if (!option) return "";
		if (typeof option === "string") return option;
		return option.name ?? String(option.key ?? "");
	};

	// when user changes selection
	const handleChange = (event, newValue) => {
		let finalValue;
		
		// SINGLE SELECT
		if (!multiple) {
			setQuery(newValue ? getLabel(newValue) : "");
			finalValue = storeValueAs === "object" ? (newValue ?? null) : (newValue?.key ?? "");
			
			if (typeof onChange === "function") {
				onChange({ target: { value: finalValue } });
			} else {
				setData((prev) => ({ ...prev, [column.key]: finalValue }));
			}
			return;
		}

		// MULTIPLE SELECT
		const lastSelected = newValue[newValue.length - 1];
		if (lastSelected?.key === SELECT_ALL.key) {
			if (storeValueAs === "object") {
				finalValue = isAllSelected ? [] : effectiveOptions.map((o) => o);
			} else {
				finalValue = isAllSelected ? [] : effectiveOptions.map((o) => o.key);
			}
			setQuery("");
			
			if (typeof onChange === "function") {
				onChange({ target: { value: finalValue } });
			} else {
				setData((prev) => ({ ...prev, [column.key]: finalValue }));
			}
			return;
		}

		const filtered = newValue.filter((opt) => opt.key !== SELECT_ALL.key);
		if (storeValueAs === "object") {
			finalValue = filtered.map((opt) => optionsList.find((o) => o.key === opt.key) ?? opt);
		} else {
			finalValue = filtered.map((opt) => opt.key);
		}
		setQuery("");
		
		if (typeof onChange === "function") {
			onChange({ target: { value: finalValue } });
		} else {
			setData((prev) => ({ ...prev, [column.key]: finalValue }));
		}
	};

	return (
		<Stack
			sx={{ width }}
			direction={isHorizontalLayout ? "row" : "column"}
			spacing={isHorizontalLayout ? 0 : "3px"}
			height={"40px"}>
			{!disableLabel && (
				<LightTooltip title={column?.name + (required ? "*" : "")} placement="bottom-start" arrow>
					<InputLabel
						sx={{
							overflow: "hidden",
							color: grey[600],
							...labelSx,
						}}>
						{column?.name} {required && <span style={{ color: "red" }}>*</span>}
					</InputLabel>
				</LightTooltip>
			)}

			<Autocomplete
				multiple={multiple}
				disabled={disabled}
				disableCloseOnSelect={multiple}
				options={optionsList}
				loading={loading}
				value={value}
				inputValue={query}
				onChange={handleChange}
				onInputChange={(event, newInputValue, reason) => {
					// user typed
					if (reason === "input") {
						setQuery(newInputValue);
					} else if (reason === "clear") {
						setQuery("");
					}
				}}
				getOptionLabel={(option) => getLabel(option)}
				isOptionEqualToValue={isOptionEqualToValue}
				renderTags={(val) => (
					<span
						style={{
							whiteSpace: "nowrap",
							overflow: "hidden",
							textOverflow: "ellipsis",
						}}>
						{Array.isArray(val) ? val.map((opt) => getLabel(opt)).join(", ") : getLabel(val)}
					</span>
				)}
				renderOption={(props, option, { selected }) => {
					const isSelectAll = option.key === SELECT_ALL.key;
					const isIndeterminate =
						isSelectAll &&
						Array.isArray(selectedKeysArray) &&
						selectedKeysArray.length > 0 &&
						selectedKeysArray.length < effectiveOptions.length;
					const isChecked = isSelectAll ? isAllSelected : selected;

					return (
						<li {...props} key={option.key}>
							{multiple && (
								<Checkbox
									checked={Boolean(isChecked)}
									indeterminate={Boolean(isIndeterminate)}
									sx={{ margin: 0, padding: "2px", mr: 1 }}
									disableRipple
								/>
							)}
							<Typography color={isSelectAll ? "green" : "inherit"}>{option.name}</Typography>
						</li>
					);
				}}
				renderInput={(params) => (
					<TextField
						{...params}
						placeholder={`Search ${column?.name}`}
						variant="outlined"
						size="small"
						fullWidth
						error={!!error}
						helperText={typeof error === "string" ? error : ""}
						InputProps={{
							...params.InputProps,
							endAdornment: (
								<>
									{loading && <CircularProgress size={20} />}
									{params.InputProps.endAdornment}
								</>
							),
						}}
					/>
				)}
				sx={{
					...sx,
					"& .MuiAutocomplete-inputRoot": {
						display: "flex",
						alignItems: "center",
						flexWrap: "nowrap",
						height: "40px",
						overflow: "hidden",
						width: "100%",
						borderRadius: isHorizontalLayout ? "0px" : undefined,
						...sx?.["& .MuiAutocomplete-inputRoot"],
					},
					"& .MuiAutocomplete-tag": {
						flex: Array.isArray(value) && value.length ? "1 1 100%" : "0 0 0%",
						width: Array.isArray(value) && value.length ? "100%" : "0%",
						overflow: "hidden",
						whiteSpace: "nowrap",
						textOverflow: "ellipsis",
						marginRight: "4px",
						transition: "all 0.3s ease",
						display: Array.isArray(value) && value.length ? "flex" : "none",
					},
					"& .MuiAutocomplete-input": {
						flex: Array.isArray(value) && value.length ? "0 0 0%" : "1 1 100%",
						minWidth: 0,
						width: Array.isArray(value) && value.length ? "1px" : "100%",
						pointerEvents: Array.isArray(value) && value.length ? "none" : "auto",
						transition: "all 0.3s ease",
						display: Array.isArray(value) && value.length ? "none" : "block",
						padding: 0,
					},
					"&.Mui-focused .MuiAutocomplete-input, &:hover .MuiAutocomplete-input":
						Array.isArray(value) && value.length
							? {
									flex: "0 0 40%",
									width: "auto",
									pointerEvents: "auto",
									display: "block",
								}
							: {},
					"&.Mui-focused .MuiAutocomplete-tag, &:hover .MuiAutocomplete-tag":
						Array.isArray(value) && value.length
							? {
									flex: "0 0 60%",
									maxWidth: "60%",
									display: "flex",
								}
							: {},
				}}
			/>
		</Stack>
	);
};

export default SearchInputFieldWithDebounce;
